import {
  Box,
  Button,
  RadioGroup,
  Typography,
  useTheme,
} from "@outschool/backpack";
import {
  CancelOneOnOneModal_RefundPolicyFragment,
  CancelOneOnOneModal_SelectedMeetingFragment,
} from "@outschool/gql-frontend-generated";
import {
  TFunction,
  Trans,
  useLocalPriceDisplay,
  useLocale,
  useTranslation,
} from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { refundPolicyUrl } from "@outschool/routes";
import {
  describeDuration,
  formatDateWithWeekday,
  startEndTime,
  weeklyDayOfWeekWithDuration,
} from "@outschool/time";
import { gql } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { ErrorMessage, ExternalLink } from "@outschool/ui-components-shared";
import {
  LoadingIcon,
  Select,
  TextArea,
} from "@outschool/ui-legacy-component-library";
import { useBooleanState } from "@outschool/ui-utils";
import React from "react";

import * as Env from "../../../shared/Env";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import {
  useCancelOneOnOneMeetingMutation,
  useCancelRecurringOneOnOneMeetingMutation,
} from "../../queries/MeetingMutations";

type RefunderRole = "parent" | "teacher";

interface CancelOneOnOneModal {
  onClose: () => void;
  onCancel: (issuedRefund: boolean, canceledFutureMeetings: boolean) => void;
  selectedMeeting: CancelOneOnOneModal_SelectedMeetingFragment;
  refundPolicy: CancelOneOnOneModal_RefundPolicyFragment | undefined;
  refunderRole: RefunderRole;
  serviceFeePercentage?: number;
}

export default function CancelOneOnOneModal({
  onClose,
  onCancel,
  selectedMeeting,
  refundPolicy,
  refunderRole,
  serviceFeePercentage = 0,
}: CancelOneOnOneModal) {
  const { t } = useTranslation("client\\components\\meetings\\CancelModal");
  const { renderCurrencyString } = useLocalPriceDisplay();
  const { timeZone } = useTimeZone();
  const theme = useTheme();

  //Cancel hooks
  const [cancelOneOnOneMeeting, { isCancelingOneOnOneMeeting }] =
    useCancelOneOnOneMeetingMutation();
  const [
    cancelRecurringOneOnOneMeeting,
    { isCancelingRecurringOneOnOneMeeting },
  ] = useCancelRecurringOneOnOneMeetingMutation();
  const [reasonForCancel, setReasonForCancel] =
    React.useState("Select a reason");
  const [confimationError, setConfirmationError] = React.useState<
    Error | string | undefined
  >();
  const [errorMessage, setErrorMessage] = React.useState<{
    reasonForCancel: undefined | string;
  }>({
    reasonForCancel: undefined,
  });
  const [message, setMessage] = React.useState<string | undefined>();
  const [
    isAllFutureMeetingsSelected,
    setAllFutureMeetingsSelected,
    clearAllFutureMeetingsSelected,
  ] = useBooleanState(false);

  const { currentUser } = useSession();

  const dateOfMeeting = formatDateWithWeekday(
    selectedMeeting.start_time,
    timeZone,
    true,
  );
  const timeOfMeeting = startEndTime(
    selectedMeeting.start_time,
    selectedMeeting.end_time,
    timeZone,
    false,
  );
  const durationOfMeeting = describeDuration(
    selectedMeeting.start_time,
    selectedMeeting.end_time,
  );
  const locale = useLocale();

  const isRecurring = !!selectedMeeting.recurring;
  const issuesRefund = selectedMeeting.refundableAmountCents > 0;

  const serviceFee = Math.ceil(
    (selectedMeeting.refundableAmountCents * serviceFeePercentage) / 100,
  );

  const refundDescription = getRefundExplanation({
    t,
    refunderRole,
    isRecurring,
    canRefund: issuesRefund,
    refundableAmount: renderCurrencyString({
      priceInCents: selectedMeeting.refundableAmountCents + serviceFee,
    }),
    refundableAmountCredits: selectedMeeting.refundableAmountCredits,
    paidAt: selectedMeeting.paidAt,
  });

  const hasValidFormValues = () => {
    const hasSetReasonForCancel = reasonForCancel != "Select a reason";
    setErrorMessage({
      ...errorMessage,
      reasonForCancel: hasSetReasonForCancel
        ? undefined
        : t("Please provide a reason why you are canceling this meeting"),
    });
    return hasSetReasonForCancel;
  };

  return (
    <Box
      flex
      sx={{
        justifyContent: "start",
        flexDirection: "column",
      }}
    >
      {isRecurring ? (
        <Typography variant="h3" sx={{ paddingY: "0.5em" }}>
          {t("Cancel a Recurring Meeting")}
        </Typography>
      ) : (
        <Typography variant="h3" sx={{ paddingY: "0.5em" }}>
          {t("Cancel a Meeting")}
        </Typography>
      )}
      {
        <Typography variant="body1" sx={{ paddingY: "0.5em" }}>
          {refundDescription}
        </Typography>
      }
      <Box
        flex
        sx={{
          backgroundColor: theme.palette.grey[50],
          justifyContent: "start",
          flexDirection: "column",
          padding: "15px",
        }}
      >
        <Typography variant="body2" sx={{ paddingY: "4px" }}>
          {t("Selected meeting time")}
        </Typography>
        <Typography variant="body1" sx={{ paddingY: "4px" }}>
          {dateOfMeeting} {timeOfMeeting} ({durationOfMeeting})
        </Typography>
        {isRecurring && (
          <Box
            flex
            sx={{
              justifyContent: "start",
              flexDirection: "column",
              padding: "4px",
            }}
          >
            <Typography variant="h5" sx={{ paddingY: "15px" }}>
              {t("Edit Recurring meetings")}
            </Typography>
            <RadioGroup
              name="Edit Recurring meetings"
              options={[
                { label: t("This meeting"), value: "false" },
                {
                  label: t("All future {{meetingTime}} meetings", {
                    meetingTime: weeklyDayOfWeekWithDuration(
                      selectedMeeting.start_time,
                      selectedMeeting.end_time,
                      timeZone,
                    ),
                  }),
                  value: "true",
                },
              ]}
              value={isAllFutureMeetingsSelected}
              onChange={(_e: any, v: string) => {
                if (v === "true") {
                  setAllFutureMeetingsSelected();
                } else {
                  clearAllFutureMeetingsSelected();
                }
              }}
            />
          </Box>
        )}
      </Box>
      <Typography variant="h6" sx={{ paddingY: "15px" }}>
        <Trans>
          Why are you cancelling this meeting?{" "}
          <Typography sx={{ color: "grey.400" }}>
            (share with Outschool)
          </Typography>
        </Trans>
      </Typography>
      <Select
        value={reasonForCancel}
        options={[
          { value: "Select a reason", label: t("Select a reason") },
          ...getReasonsToCancel({ t, refunderRole }),
          { value: "Other", label: t("Other") },
        ]}
        includeBlank={false}
        onChange={async (value: string) => {
          setReasonForCancel(value);
        }}
        sx={{
          borderRadius: "4px",
          borderColor: errorMessage.reasonForCancel
            ? theme.palette.error[600]
            : theme.palette.grey[200],
          border: "1px solid",
          color: theme.palette.text.primary,
        }}
      />
      {errorMessage.reasonForCancel && (
        <Typography
          variant="body1"
          sx={{ paddingY: "0.5em", color: theme.palette.error[600] }}
        >
          {errorMessage.reasonForCancel}
        </Typography>
      )}
      <Typography variant="h6" sx={{ paddingY: "1em" }}>
        {refunderRole === "teacher"
          ? t("Message the family")
          : t("Message the teacher")}{" "}
        <Typography sx={{ color: "grey.400" }}>({t("Optional")})</Typography>
      </Typography>
      <TextArea
        value={message}
        onChange={async (e: React.FormEvent<HTMLTextAreaElement>) => {
          setMessage((e.target as HTMLTextAreaElement).value);
        }}
        placeholder={
          refunderRole === "teacher"
            ? t("Let the family know the reason")
            : t("Let the teacher know the reason")
        }
        rows={4}
        maxRows={4}
        showCount={false}
      />
      <Typography variant="body1" sx={{ paddingY: "2em" }}>
        {refunderRole === "teacher" ? (
          t(
            "Families will be notified of the schedule change. Are you sure you want to proceed?",
          )
        ) : (
          <Trans t={t}>
            This class has a{" "}
            <ExternalLink url={refundPolicyUrl(locale)}>
              {{ refundPolicy: refundPolicy?.name } as any}
            </ExternalLink>{" "}
            policy.{" "}
            {{
              refundDescription:
                selectedMeeting.refundableAmountCredits ||
                (currentUser?.canPayWithSubscriptionCredits &&
                  selectedMeeting.refundableAmountCents === 0)
                  ? refundPolicy?.creditDescription
                  : refundPolicy?.description,
            }}
          </Trans>
        )}
      </Typography>
      {confimationError && (
        <ErrorMessage
          value={confimationError}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      )}
      <Box
        flex
        sx={{
          justifyContent: "start",
          flexDirection: "row",
        }}
      >
        <Button
          color="error"
          variant="contained"
          disabled={
            isCancelingOneOnOneMeeting || isCancelingRecurringOneOnOneMeeting
          }
          onClick={async () => {
            if (!hasValidFormValues()) {
              return;
            }
            if (isAllFutureMeetingsSelected) {
              try {
                await cancelRecurringOneOnOneMeeting({
                  uid: selectedMeeting.uid,
                  cancelReason: reasonForCancel,
                  // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                  cancelNote: message,
                  reducesTeacherPayout: true,
                });
                onCancel(issuesRefund, true);
                onClose();
              } catch (err) {
                setConfirmationError(
                  t(
                    "Sorry, that didn’t work. We’ll look into it. You can reload the page or contact support@outschool.com.",
                  ),
                );
                OsPlatform.captureError(err, {
                  tags: { component: Component.EnrollmentsParent },
                });
              }
            } else {
              try {
                await cancelOneOnOneMeeting({
                  uid: selectedMeeting.uid,
                  cancelReason: reasonForCancel,
                  // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                  cancelNote: message,
                  reducesTeacherPayout: true,
                });
                onCancel(issuesRefund, false);
                onClose();
              } catch (err) {
                setConfirmationError(
                  t(
                    "Sorry, that didn’t work. We’ll look into it. You can reload the page or contact support@outschool.com.",
                  ),
                );
                OsPlatform.captureError(err, {
                  tags: { component: Component.EnrollmentsParent },
                });
              }
            }
          }}
        >
          {isCancelingOneOnOneMeeting || isCancelingRecurringOneOnOneMeeting ? (
            <>
              <LoadingIcon /> {t("Deleting Meeting(s)")}
            </>
          ) : (
            <>
              {t("Cancel the Meeting", {
                count: isAllFutureMeetingsSelected ? 2 : 1, // arbitrary number 2 to force pluralization
                defaultValue_plural: "Cancel the Meetings",
              })}
            </>
          )}
        </Button>
        <Button color="inherit" variant="text" onClick={onClose}>
          {t("No, Keep the Meeting", {
            count: isAllFutureMeetingsSelected ? 2 : 1, // arbitrary number 2 to force pluralization
            defaultValue_plural: "No, Keep the Meetings",
          })}
        </Button>
      </Box>
    </Box>
  );
}

CancelOneOnOneModal.fragments = {
  selectedMeeting: gql`
    fragment CancelOneOnOneModal_selectedMeeting on Meeting {
      uid
      start_time
      end_time
      recurring
      paidAt
      refundableAmountCents
      refundableAmountCredits
    }
  `,
  refundPolicy: gql`
    fragment CancelOneOnOneModal_refundPolicy on RefundPolicy {
      name
      description
      creditDescription
    }
  `,
};

function getReasonsToCancel({ t, refunderRole }: $TSFixMe) {
  return refunderRole === "parent"
    ? [
        {
          value: "Class did not meet our expectations",
          label: t("Class did not meet our expectations"),
        },
        {
          value: "Teacher is not a good fit",
          label: t("Teacher is not a good fit"),
        },
        { value: "Schedule conflict", label: t("Schedule conflict") },
        {
          value: "No longer interested in this class",
          label: t("No longer interested in this class"),
        },
        {
          value: "Teacher asked to cancel",
          label: t("Teacher asked to cancel"),
        },
      ]
    : [
        {
          value: "Medical emergency and/or illness",
          label: t("Medical emergency and/or illness"),
        },
        {
          value: "Bereavement",
          label: t("Bereavement"),
        },
        {
          value: "Extreme weather and/or power outages",
          label: t("Extreme weather and/or power outages"),
        },
        {
          value: "Parent requested to cancel and refund",
          label: t("Parent requested to cancel and refund"),
        },
        {
          value: "Parent requested to reschedule",
          label: t("Parent requested to reschedule"),
        },
        {
          value: "Personal schedule conflict",
          label: t("Personal schedule conflict"),
        },
      ];
}

function getRefundExplanation({
  t,
  isRecurring,
  canRefund,
  refundableAmount,
  refundableAmountCredits,
  refunderRole,
  paidAt,
}: {
  t: TFunction;
  isRecurring: boolean;
  canRefund: boolean;
  refundableAmount: string;
  refundableAmountCredits: number | null;
  refunderRole: RefunderRole;
  paidAt?: Date;
}) {
  if (!paidAt) {
    if (refunderRole === "teacher") {
      if (isRecurring) {
        return t(
          "There’s no further action required on your part. If you cancel all future meetings, we will stop the future weekly payment.",
        );
      } else {
        return t("There’s no further action required on your part.");
      }
    } else {
      if (isRecurring) {
        return t(
          "If you cancel all future meetings, we will stop the future weekly payment.",
        );
      } else {
        return null;
      }
    }
  }
  if (refunderRole === "teacher") {
    if (isRecurring) {
      return t(
        "There’s no further action required on your part. We will refund any amount they already paid. If you cancel all future meetings, we will stop the future weekly payment.",
      );
    } else {
      return t(
        "There’s no further action required on your part. We will refund any amount they already paid.",
      );
    }
  } else {
    if (canRefund && refundableAmountCredits) {
      if (isRecurring) {
        return t(
          "You are eligible for a refund of {{numCredits}} credits. {{numCredits}} credits will be returned to your balance. If you cancel all future meetings, we will stop weekly credit deductions.",
          { numCredits: refundableAmountCredits },
        );
      } else {
        return t(
          "You are eligible for a refund of {{numCredits}} credits. {{numCredits}} credits will be returned to your balance",
          { numCredits: refundableAmountCredits },
        );
      }
    } else if (canRefund) {
      if (isRecurring) {
        return t(
          "You are eligible for a refund of {{Price}}. This will issue a refund of {{Price}}. If you cancel all future meetings, we will stop weekly charging.",
          { Price: refundableAmount },
        );
      } else {
        return t(
          "You are eligible for a refund of {{Price}}. This will issue a refund of {{Price}}.",
          { Price: refundableAmount },
        );
      }
    } else {
      if (isRecurring) {
        return t(
          "You are NOT eligible for a refund. The following meeting will be canceled and will NOT be refunded. If you cancel all future meetings, we will stop the future weekly payment.",
        );
      } else {
        return t(
          "You are NOT eligible for a refund. The following meeting will be canceled and will NOT be refunded.",
        );
      }
    }
  }
}
